import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faShieldHalved } from "@fortawesome/pro-regular-svg-icons";
import { type UseMutateFunction } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import React, { useMemo } from "react";
import { v4 as uuidV4 } from "uuid";

import { useConnections } from "src/api/connectApi/queries";
import type {
  Connection,
  CustomProviderResource,
  ResourceConfig,
} from "src/api/connectApi/types";
import { Schema } from "src/api/flowTypes";
import type { FlowVersion } from "src/api/flowTypes";
import { SchemaUpdate } from "src/api/flowVersionQueries";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { ConnectionDataSourcesValues } from "src/baseConnectionNode/types";
import {
  CaseFlowConnectionActions,
  ensureCaseFlowConnection,
  generateCaseFlowConnectionName,
} from "src/cases/caseFlowConnections";
import {
  useCaseTemplateDataSchema,
  useEnsureCaseTemplateDataSchema,
  type TemplateWithVersion,
} from "src/cases/queries/templates";
import { getTemplateIcon as getTemplateIconFromUtils } from "src/cases/templates/utils";
import {
  CaseTemplateAttributes,
  JsonApiResourceObjectCaseTemplateAttributesBaseModel,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { CustomConnectionNodeData } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { toastActions } from "src/design-system/Toast/utils";
import {
  useAuthoringContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import { CaseFieldAccessInfo } from "src/schema/schemaEditor/CaseFieldAccessInfo";
import { CaseSchemaHint } from "src/schema/schemaEditor/CaseSchemaHint";
import { CaseTemplateSelector } from "src/schema/schemaEditor/CaseTemplateSelector";
import {
  TemplateMetadata,
  transformTemplateToFlowSchema,
  createSelectedSchema,
  SelectedTemplateInfo,
} from "src/schema/schemaEditor/caseSchemaUtils";
import { SchemaEditorProps } from "src/schema/schemaEditor/types";
import { useCaseSchemaEditorState } from "src/schema/schemaEditor/useCaseSchemaEditorState";
import {
  getTemplateMetadata,
  useCaseTemplateInputSchemaError,
} from "src/schema/schemaEditor/useCaseTemplateInputSchemaValidation";
import {
  ARCHIVED_TOAST_ID,
  CHANGED_TOAST_ID,
} from "src/schema/schemaEditor/useErrorToasts";
import { useUpdateSchemaWithFlowTags } from "src/schema/schemaEditor/useUpdateSchemaWithFlowTags";
import { GraphStore } from "src/store/GraphStore";
import { useGraphStore } from "src/store/StoreProvider";
import { logger } from "src/utils/logger";

export type CaseSchemaEditorProps = SchemaEditorProps & {
  mutateSchema: UseMutateFunction<
    FlowVersion,
    AxiosError,
    SchemaUpdate,
    unknown
  >;
};

export type CaseSchemaWithMetadata = Schema & {
  _template_metadata?: TemplateMetadata;
};

type DropdownTemplate = JsonApiResourceObjectCaseTemplateAttributesBaseModel & {
  versions?: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[];
};

type DropdownSelection = {
  templateId: string;
  versionId: string;
};

const CASE_DATA_PATH = "data.case";

const getTemplateIcon = (icon: string | null | undefined): IconProp => {
  return getTemplateIconFromUtils(icon) ?? faShieldHalved;
};

const transformTemplatesToDropdownFormat = (
  templatesData: TemplateWithVersion[] | undefined,
): DropdownTemplate[] => {
  if (!templatesData) return [];

  const templateMap: Record<string, DropdownTemplate> = {};

  templatesData.forEach((template) => {
    if (!templateMap[template.templateId]) {
      templateMap[template.templateId] = {
        id: template.templateId,
        type: "case_template",
        attributes: template.template.attributes,
        versions: [],
      };
    }

    const dropdownTemplate = templateMap[template.templateId];
    if (dropdownTemplate?.versions) {
      dropdownTemplate.versions.push(template.version);
    }
  });

  return Object.values(templateMap);
};

const extractCurrentTemplate = (
  currentSchema: Schema | undefined,
  templatesData: TemplateWithVersion[] | undefined,
): SelectedTemplateInfo | null => {
  const caseSchema = currentSchema?.properties?.case as
    | CaseSchemaWithMetadata
    | undefined;

  if (!currentSchema?.properties?.case || !caseSchema) {
    return null;
  }

  const metadata = caseSchema._template_metadata;

  if (metadata?.templateId && metadata?.versionId) {
    const template = templatesData?.find(
      (t) =>
        t.templateId === metadata.templateId &&
        t.versionId === metadata.versionId,
    );

    if (template) {
      return {
        templateId: template.templateId,
        versionId: template.versionId,
        templateName: template.templateName,
        versionName: template.versionName,
        icon: getTemplateIcon(template.icon),
      };
    }

    // If template not found in templatesData but we have metadata (possibly archived),
    // use the saved names from metadata
    if (metadata.templateName && metadata.versionName) {
      return {
        templateId: metadata.templateId,
        versionId: metadata.versionId,
        templateName: metadata.templateName,
        versionName: metadata.versionName,
        icon: getTemplateIcon(null),
      };
    }
  }

  return {
    templateId: "unknown",
    versionId: "unknown",
    templateName: "Case Template",
    versionName: "Unknown",
    icon: getTemplateIcon(null),
  };
};

export const CaseSchemaEditor: React.FC<CaseSchemaEditorProps> = ({
  isReadonly,
  formMethods: _formMethods,
  mutateSchema,
}) => {
  const { workspace } = useWorkspaceContext();
  const { version } = useAuthoringContext();
  const graphStore = useGraphStore();
  const currentSchema = version?.input_schema;
  const { templateId, versionId } = getTemplateMetadata(currentSchema);
  const error = useCaseTemplateInputSchemaError(currentSchema);

  const updateSchemaWithFlowTags = useUpdateSchemaWithFlowTags({
    mutateSchema,
  });

  const { data: templateSchemaResponse, isLoading: isLoadingSchema } =
    useCaseTemplateDataSchema(templateId, versionId, {
      throwOnError: false,
    });

  const { promise: connectionsPromise } = useConnections(workspace.base_url, {
    showInternalResources: true,
  });

  const {
    createConnectionMutation,
    editConnectionMutation,
    templatesData,
    isLoadingTemplates,
  } = useCaseSchemaEditorState();

  const selectedTemplate = useMemo(
    () => extractCurrentTemplate(currentSchema, templatesData),
    [currentSchema, templatesData],
  );

  const selectedSchema = useMemo(
    () =>
      createSelectedSchema(
        templateSchemaResponse,
        templatesData,
        selectedTemplate,
        currentSchema,
      ),
    [templateSchemaResponse, templatesData, selectedTemplate, currentSchema],
  );

  const dropdownTemplates = useMemo(
    () => transformTemplatesToDropdownFormat(templatesData),
    [templatesData],
  );

  const handleTemplateClear = async () => {
    try {
      await updateSchemaWithFlowTags({
        type: "input",
        schema: {
          $schema: "https://json-schema.org/draft/2020-12/schema",
          type: "object",
          properties: {},
          required: [],
          sensitive: [],
          order: [],
        },
      });
    } catch (error) {
      logger.error("Error clearing template schema:", error);

      toastActions.failure({
        title: "Unable to clear template schema",
        description: "Please refresh the page and try again.",
      });
    }
  };

  const ensureCaseTemplateDataSchema = useEnsureCaseTemplateDataSchema();

  const handleTemplateSelection = async (selection: DropdownSelection) => {
    const { templateId, versionId } = selection;
    const selectedTemplateData = templatesData?.find(
      (t) => t.templateId === templateId && t.versionId === versionId,
    );

    if (!selectedTemplateData) return;

    // Dismiss toasts if they're still visible
    toastActions.dismiss(ARCHIVED_TOAST_ID);
    toastActions.dismiss(CHANGED_TOAST_ID);

    const templateSchemaResponse = await ensureCaseTemplateDataSchema(
      templateId,
      versionId,
    );

    const caseJsonSchema =
      templateSchemaResponse.data.attributes.case_json_schema;

    if (selectedTemplateData) {
      const wrappedSchema = transformTemplateToFlowSchema(caseJsonSchema, {
        templateId: selectedTemplateData.templateId,
        versionId: selectedTemplateData.versionId,
        etag: selectedTemplateData.version.attributes.etag,
        templateName: selectedTemplateData.templateName,
        versionName: selectedTemplateData.versionName,
      });

      try {
        await updateSchemaWithFlowTags({
          type: "input",
          schema: wrappedSchema,
        });
      } catch (error) {
        logger.error("Error updating flow tags:", error);

        toastActions.failure({
          title: "Unable to select Case template",
          description: "Please refresh the page and try again.",
        });
      }
    } else {
      logger.error("Selected template data not found:", {
        templateId,
        versionId,
      });
    }

    if (isReadonly) return;

    try {
      const connectionsData = await connectionsPromise;

      const caseFlowConnectionId = connectionsData.find(
        (c) =>
          c.name === generateCaseFlowConnectionName(workspace.id) &&
          c.provider === "custom",
      )?.id;

      const hasSaveCaseConnectionNode = graphStore.nodesArray.some(
        (node) =>
          node.type === NODE_TYPE.CUSTOM_CONNECTION_NODE &&
          (node.data as CustomConnectionNodeData)?.connectionId ===
            caseFlowConnectionId,
      );

      if (hasSaveCaseConnectionNode) return;

      const connectionActions = {
        create: createConnectionMutation.mutateAsync,
        update: editConnectionMutation.mutateAsync,
      };

      await triggerCustomConnectionNodeCreation({
        selectedTemplate: selectedTemplateData.template.attributes,
        workspace,
        connections: connectionsData,
        connectionActions,
        graphStore,
        setClickedVisibleEdgeId: graphStore.setClickedVisibleEdgeId,
        getClickedVisibleEdgeId: (): string | null =>
          graphStore.clickedVisibleEdgeId ?? null,
      });

      toastActions.success({
        title: "Custom connection added",
        description: `A custom connection node has been added to your flow for the ${selectedTemplateData.template.attributes.template_name} template.`,
      });
    } catch {
      toastActions.failure({
        title: "Unable to add custom connection",
        description:
          "The case template was configured successfully, but we couldn't automatically add the connection node. You can add it manually from the node palette.",
      });
    }
  };

  return (
    <div data-loc="case-schema-editor-container">
      <CaseTemplateSelector
        dropdownTemplates={dropdownTemplates}
        error={error}
        formMethods={_formMethods}
        isLoadingSchema={isLoadingSchema}
        isLoadingTemplates={isLoadingTemplates}
        isReadonly={isReadonly}
        mutateSchema={mutateSchema}
        selectedTemplate={selectedTemplate}
        onTemplateClear={handleTemplateClear}
        onTemplateSelection={handleTemplateSelection}
      />
      {selectedTemplate && <CaseFieldAccessInfo />}
      {isLoadingSchema && selectedTemplate && (
        <div className="mt-4">
          <SkeletonPlaceholder height="h-32" />
        </div>
      )}
      {selectedSchema && !isLoadingSchema && (
        <CaseSchemaHint
          rootPath={CASE_DATA_PATH}
          selectedSchema={selectedSchema}
        />
      )}
    </div>
  );
};

const getOutputEdge = (graphStore: GraphStore): string => {
  const outputNode = graphStore.nodesArray.find(
    (n) => n.type === NODE_TYPE.OUTPUT_NODE,
  );
  if (!outputNode) {
    throw new Error("No output node found in the graph store.");
  }

  const incomingEdge = graphStore.edgesArray.find(
    (e) => e.target === outputNode.id,
  );
  if (!incomingEdge) {
    throw new Error(`No incoming edge found for output node: ${outputNode.id}`);
  }
  return incomingEdge.id;
};

export const triggerCustomConnectionNodeCreation = async (params: {
  selectedTemplate: CaseTemplateAttributes;
  workspace: WorkspaceDataplane;
  connections: Connection[];
  connectionActions: CaseFlowConnectionActions;
  graphStore: GraphStore;
  setClickedVisibleEdgeId: (id: string | null) => void;
  getClickedVisibleEdgeId: () => string | null;
}) => {
  const {
    workspace,
    connections,
    connectionActions,
    graphStore,
    setClickedVisibleEdgeId,
    getClickedVisibleEdgeId,
  } = params;

  // 1. Ensure the custom connection exists for this flow
  const connection = await ensureCaseFlowConnection(
    workspace,
    connectionActions,
    connections,
  );
  if (!connection) {
    throw new Error(
      `Failed to ensure case flow connection for workspace: ${workspace.id}`,
    );
  }

  // 2. Find the resource config for 'http', skipping 'probe'
  const resourceConfig = connection.resource_configs.find(
    (rc: ResourceConfig) => rc.resource !== "probe" && rc.resource === "http",
  );
  if (!resourceConfig) {
    throw new Error(
      `No valid resource config (http) found for connection: ${connection.id}. Available resources: ${connection.resource_configs.map((rc) => rc.resource).join(", ")}`,
    );
  }

  // 3. Find the output node and its incoming edge
  const incomingEdgeId1 = getOutputEdge(graphStore);

  // 4. Capture current state after network requests to ensure it's fresh
  const savedClickedEdgeId = getClickedVisibleEdgeId();

  // 5. Temporarily set clickedVisibleEdgeId, add node at output's incoming edge, then restore
  setClickedVisibleEdgeId(incomingEdgeId1);

  // 6. Add code node for saving case
  try {
    const codeNodeId = await graphStore.addNode({
      nodeType: NODE_TYPE.CODE_NODE,
    });
    if (!codeNodeId) {
      throw new Error("Failed to create code node for saving case.");
    }
    graphStore.updateNode({
      newData: {
        src: `data.case_update = {
    "atomic:operations": [{
        "op": "update",
        "data": {}
    } for block in ["some", "other"]]
}
data.tktl_bearer_header = f"Bearer {__auth_jwt}"
data.tktl_running_action_id = meta.entity_id    
`,
      },
      newName: "Select Case Updates",
      nodeId: codeNodeId,
    });
  } catch (error) {
    logger.error("Failed to add code node for saving case:", error);
    setClickedVisibleEdgeId(savedClickedEdgeId);
    throw new Error("Failed to add code node for saving case: " + error);
  }

  // 7. Get output edge after adding code node
  const incomingEdgeId2 = getOutputEdge(graphStore);
  setClickedVisibleEdgeId(incomingEdgeId2);

  try {
    // Use taktile icon for case custom connections
    const taktileMediaKey = "taktile-icon";

    await graphStore.addNode({
      nodeType: NODE_TYPE.CUSTOM_CONNECTION_NODE,
      providerResource: {
        provider: "custom",
        resource: resourceConfig.resource,
      } as CustomProviderResource,
      connectionId: connection.id,
      resourceConfigId: resourceConfig.id,
      mediaKey: taktileMediaKey,
      name: "Save case",
      optionals: {
        verb: "post",
        path: {
          id: uuidV4(),
          expression: "/api/v1/cases/{data.case.id}/operations",
        },
        params: [],
        // HTTP headers for the custom connection node
        // Each header is a KeyValuePair with:
        // - key/value: the actual HTTP header name and value
        // - id_key/id_value: unique identifiers for the UI to track changes to this specific header pair
        headers: [
          {
            key: "Authorization",
            value: "data.tktl_bearer_header",
            id_key: uuidV4(), // Unique ID for tracking this header's key field in the UI
            id_value: uuidV4(), // Unique ID for tracking this header's value field in the UI
          },
          {
            key: "RunningActionID",
            value: "data.tktl_running_action_id",
            id_key: uuidV4(),
            id_value: uuidV4(),
          },
        ],
        body: {
          id: uuidV4(),
          value: {
            selected: "raw",
            raw: "data.case_update",
          },
        },
        response: {
          id: uuidV4(),
          mapped_to: "tktl_save_case_response",
          active: true,
        },
        config: {
          environments_config: {
            production: ConnectionDataSourcesValues.production,
            sandbox_mode_data_source: ConnectionDataSourcesValues.production,
            authoring_mode_data_source: ConnectionDataSourcesValues.production,
          },
          testing: {
            use_cached_reports: false,
            use_fallback_live_connection: false,
          },
          retry_config: { active: false },
          caching: { active: false, max_age_seconds: "86400" },
          timeout: { active: false, timeout_seconds: "15" },
          error: { allow_4xx: false, allow_5xx: false },
          has_raw_requests_enabled_in_node: true,
        },
      },
    });
  } finally {
    // Always restore the saved state, even if addNode fails
    setClickedVisibleEdgeId(savedClickedEdgeId);
  }
};
